import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export const DASHBOARD_TABS = ["overview", "insights", "advanced"];
export const DASHBOARD_RANGES = [7, 30, 90];
export const DEFAULT_RANGE = 30;

// Tab and range live in the URL (/dashboard?tab=insights&range=30) so a
// refresh, the back button and a shared link all land on the same view. An
// unknown value falls back to the default rather than reaching the API, which
// answers 422 for a range other than 7/30/90.
export function readDashboardParams(searchParams) {
  const tab = searchParams.get("tab");
  const range = Number(searchParams.get("range"));
  return {
    tab: DASHBOARD_TABS.includes(tab) ? tab : "overview",
    range: DASHBOARD_RANGES.includes(range) ? range : DEFAULT_RANGE,
  };
}

export default function useDashboardParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, range } = readDashboardParams(searchParams);

  const update = useCallback(
    (changes, replace) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) => {
            next.set(key, String(value));
          });
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  // A tab is a place worth a history entry; a range tweak is not.
  const setTab = useCallback((value) => update({ tab: value }, false), [update]);
  const setRange = useCallback((value) => update({ range: value }, true), [update]);

  return { tab, range, setTab, setRange };
}
